import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Table, Card, Button, Space, Tag, Descriptions, Modal, message, Input, Select } from 'antd'
import { SearchOutlined, ReloadOutlined, EyeOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'

interface SmartTrayOrder {
  id: number
  order_no: string
  other_uid: string
  plate_no: string
  status: number
  total_fee: number
  mode: number
  catering_type: number
  product_count: number
  start_dinner_time: string
  closed_at: string
  created_at: string
  items?: SmartTrayOrderItem[]
}

interface SmartTrayOrderItem {
  id: number
  name: string
  price: number
  payable: number
  weight: number
  product_type: number
  card_no: string
}

export default function SmartTrayOrders() {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<SmartTrayOrder[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [keyword, setKeyword] = useState('')
  const [statusFilter, setStatusFilter] = useState<number | undefined>(undefined)
  const [detailVisible, setDetailVisible] = useState(false)
  const [currentOrder, setCurrentOrder] = useState<SmartTrayOrder | null>(null)
  const [orderItems, setOrderItems] = useState<SmartTrayOrderItem[]>([])
  const [searchParams, setSearchParams] = useSearchParams()
  const filterUserId = useMemo(() => {
    const uid = searchParams.get('user_id')
    return uid ? parseInt(uid) : undefined
  }, [searchParams])

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      let url = `/api/smart-tray/orders?page=${page}&page_size=${pageSize}`
      if (keyword) url += `&keyword=${encodeURIComponent(keyword)}`
      if (statusFilter !== undefined) url += `&status=${statusFilter}`
      if (filterUserId) url += `&user_id=${filterUserId}`
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      }).then(r => r.json())
      
      if (res.code === 0 || res.code === 200) {
        setData(res.data?.list || [])
        setTotal(Number(res.data?.total) || 0)
      }
    } catch {
      message.error('加载订单列表失败')
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, keyword, statusFilter, filterUserId])

  useEffect(() => { loadData() }, [loadData])

  const clearUserIdFilter = () => {
    setSearchParams(new URLSearchParams())
  }

  const loadOrderDetail = async (order: SmartTrayOrder) => {
    setCurrentOrder(order)
    setDetailVisible(true)
    
    try {
      const res = await fetch(`/api/smart-tray/orders/${order.id}/items`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      }).then(r => r.json())
      
      if (res.code === 0 || res.code === 200) {
        setOrderItems(res.data || [])
      }
    } catch {
      message.error('加载订单明细失败')
    }
  }

  const getCateringTypeText = (type: number) => {
    const map: Record<number, string> = { 0: '凌晨夜宵', 1: '早餐', 2: '午餐', 3: '晚餐', 4: '宵夜' }
    return map[type] || '未知'
  }

  const columns: ColumnsType<SmartTrayOrder> = [
    { title: '订单号', dataIndex: 'order_no', width: 200 },
    { title: '用户ID', dataIndex: 'other_uid', width: 120 },
    { title: '餐盘号', dataIndex: 'plate_no', width: 100 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (v: number) => v === 1 ? <Tag color="blue">进行中</Tag> : <Tag color="green">已结束</Tag>
    },
    {
      title: '总金额',
      dataIndex: 'total_fee',
      width: 100,
      render: (v: number) => v ? `¥${Number(v).toFixed(2)}` : '-'
    },
    {
      title: '手机号',
      key: 'phone',
      width: 120,
      render: (_: any, record: any) => record.user?.member?.phone || record.user?.phone || '-'
    },
    {
      title: '昵称',
      key: 'nickname',
      width: 100,
      render: (_: any, record: any) => record.user?.member?.nickname || '-'
    },
    {
      title: '计费模式',
      dataIndex: 'mode',
      width: 100,
      render: (v: number) => v === 1 ? '按重量' : '按次'
    },
    {
      title: '餐别',
      dataIndex: 'catering_type',
      width: 100,
      render: (v: number) => getCateringTypeText(v)
    },
    { title: '商品数量', dataIndex: 'product_count', width: 100 },
    { title: '开始时间', dataIndex: 'start_dinner_time', width: 180 },
    { title: '结束时间', dataIndex: 'closed_at', width: 180 },
    {
      title: '操作',
      width: 100,
      render: (_: any, record: SmartTrayOrder) => (
        <Button type="link" icon={<EyeOutlined />} onClick={() => loadOrderDetail(record)}>
          详情
        </Button>
      )
    }
  ]

  const itemColumns: ColumnsType<SmartTrayOrderItem> = [
    { title: '商品名称', dataIndex: 'name' },
    {
      title: '类型',
      dataIndex: 'product_type',
      render: (v: number) => v === 1 ? '按件' : '按重量'
    },
    { title: '重量(g)', dataIndex: 'weight' },
    {
      title: '单价',
      dataIndex: 'price',
      render: (v: number) => v ? `¥${Number(v).toFixed(2)}` : '-'
    },
    {
      title: '小计',
      dataIndex: 'payable',
      render: (v: number) => v ? `¥${Number(v).toFixed(2)}` : '-'
    },
    { title: '餐盘号', dataIndex: 'card_no' }
  ]

  return (
    <Card
      title="智慧餐盘订单管理"
      extra={
        <Space>
          {filterUserId && (
            <Tag closable onClose={clearUserIdFilter} color="blue">
              筛选用户ID: {filterUserId}
            </Tag>
          )}
          <Input
            placeholder="搜索订单号/用户ID"
            prefix={<SearchOutlined />}
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            onPressEnter={() => { setPage(1); loadData() }}
            style={{ width: 200 }}
          />
          <Select
            placeholder="订单状态"
            allowClear
            value={statusFilter}
            onChange={v => { setStatusFilter(v); setPage(1) }}
            style={{ width: 120 }}
          >
            <Select.Option value={1}>进行中</Select.Option>
            <Select.Option value={2}>已结束</Select.Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={loadData}>刷新</Button>
        </Space>
      }
    >
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (p, ps) => { setPage(p); setPageSize(ps) }
        }}
      />

      <Modal
        title="订单详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={800}
      >
        {currentOrder && (
          <>
            <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="订单号">{currentOrder.order_no}</Descriptions.Item>
              <Descriptions.Item label="状态">
                {currentOrder.status === 1 ? <Tag color="blue">进行中</Tag> : <Tag color="green">已结束</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="用户ID">{currentOrder.other_uid}</Descriptions.Item>
              <Descriptions.Item label="餐盘号">{currentOrder.plate_no}</Descriptions.Item>
              <Descriptions.Item label="总金额">
                {currentOrder.total_fee ? `¥${Number(currentOrder.total_fee).toFixed(2)}` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="餐别">
                {getCateringTypeText(currentOrder.catering_type)}
              </Descriptions.Item>
            </Descriptions>

            <Table
              rowKey="id"
              columns={itemColumns}
              dataSource={orderItems}
              pagination={false}
              size="small"
              title={() => <strong>商品明细</strong>}
            />
          </>
        )}
      </Modal>
    </Card>
  )
}
